import type { ReactNode } from 'react';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import LinkIcon from '@mui/icons-material/Link';
import { useTranslation } from 'react-i18next';

export type ValueSource = 'inherited' | 'local';

/**
 * A value with its PROVENANCE, because the value alone does not say where it came from.
 *
 * An inherited policyholder looks identical to a local one — same partner id, same column — and
 * the difference is that nobody can change it here: it belongs to the Gruppen-Police and is
 * resolved at read (decision D7). Writing it onto the member would be the materialization that
 * decision forbids, so the marker is the only thing that can carry the distinction.
 *
 * The icon is never the only carrier: the word is there too, for a reader who cannot see it.
 */
export function InheritedValue({
  value,
  source,
}: {
  readonly value: ReactNode;
  readonly source: ValueSource;
}): ReactNode {
  const { t } = useTranslation('insurance');
  if (source === 'local') return <>{value}</>;

  return (
    <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
      <span>{value}</span>
      <Tooltip title={t('groupPolicies.policies.inheritedHint')}>
        <Stack direction="row" spacing={0.25} sx={{ alignItems: 'center' }}>
          <LinkIcon fontSize="inherit" color="action" />
          <Typography variant="caption" color="text.secondary">
            {t('groupPolicies.policies.inherited')}
          </Typography>
        </Stack>
      </Tooltip>
    </Stack>
  );
}
